/**
 * FTGS sample dataset discovery - each playable dataset lives in its own
 * `uploads/<id>/` folder (segment_*.ply, cameras.txt, images.txt, a
 * thumbnail image) plus a `dataset.json` describing it. There is no backend
 * here (see package.json's `serve` script - a plain static file server), so
 * a directory listing isn't possible: `uploads/datasets.json` is the static
 * index of which folders exist, maintained by hand whenever a dataset is
 * added (see uploads/README.md).
 */

import type { PlaybackTimingConfig } from './playback-timing';

type FtgsDatasetConfig = {
    /** folder name under uploads/, also used as its picker id */
    id: string;
    /** display label for the picker button */
    label: string;
    /** COLMAP camera name (images.txt) used for the initial hero-camera pose, e.g. "c006" */
    heroCamera: string;
    /**
     * Initial camera position in WORLD coordinates - exactly the numbers the
     * stats overlay prints as `Camera Position`, typed straight back in.
     *
     * Takes precedence over the `camera-spawn.txt` / `images.txt` COLMAP
     * files. Those exist for datasets reconstructed from a real capture rig,
     * where the file is genuine COLMAP output; a GSC-VSEI delivery ships no
     * calibration at all, so its "hero pose" was only ever one hand-picked
     * viewpoint dressed up in COLMAP's format. That dressing cost a sign
     * trap - COLMAP stores the world->camera translation, not the camera,
     * so the position had to be re-encoded as `-R^T·t` and then flipped
     * again by the viewer's Y-up correction, leaving x inverted and y/z not
     * (the two flips cancel for y and z, but nothing cancels x). This field
     * skips both conversions.
     */
    cameraSpawn?: [number, number, number];
    /**
     * Initial look-at point in WORLD coordinates - the numbers the stats
     * overlay prints as `Camera Target`, typed straight back in (2026-10-21).
     * Without it the start view aims at the splats' median centre, so a
     * `cameraSpawn` copied after a right-drag pan (which moves the look-at
     * point along with the camera) comes back at the right place but turned
     * towards that centre. Together with `cameraSpawn` it restores the view
     * exactly. Also applies on the COLMAP hero-pose path; ignored when it
     * coincides with the camera position.
     */
    cameraTarget?: [number, number, number];
    /**
     * Starting Root Rotation, `[x, y, z]` in degrees - the stats panel's
     * Advanced > Root Rotation values, typed straight in (2026-10-26). The
     * whole splat set turns about the fixed world axes, X then Y then Z,
     * around the world origin (FtgsSplatSceneNode.setRootRotation). Applied
     * when this dataset is opened; a Rate / Source / Velocity Quant switch
     * within it keeps whatever the panel holds. Absent or malformed =
     * 0 0 0 (malformed also logs a warning).
     */
    rootRotation?: [number, number, number];
    /** number of segment_NNN.ply files, 000-indexed */
    segmentCount: number;
    /**
     * Opt in to keeping EVERY segment's GPU buffer resident for the life of
     * the dataset, so a segment swap rebinds instead of re-uploading - see
     * FtgsSplatSceneNode's `residentSegments` for the measurements behind
     * it and the VRAM it costs (segmentCount x segment size, e.g. 780MB for
     * musical's 15 x 52MB).
     *
     * Only set this for a SHORT sequence that swaps segments faster than
     * the streaming path can feed it - in practice a per-frame PLY sequence.
     * A normal dataset (one segment, or segments a second long) gains
     * nothing and should leave it off.
     */
    residentSegments?: boolean;
    /** playback seconds each segment covers (see segment-manifest.ts's module doc) */
    segmentDuration: number;
    /** still-image filename (relative to the dataset folder) for the picker thumbnail, e.g. "thumbnail.jpg" */
    thumbnail?: string;
    /**
     * Aim the initial camera along the hero camera's OWN COLMAP view
     * direction, instead of pointing it at the scene's median centre.
     *
     * The default framing puts the camera at the COLMAP position but has it
     * look at the splats' median centre, which is the more forgiving choice
     * for a dataset whose capture rig wasn't aimed at what you want to see.
     * Set this when the goal is to reproduce a reference player's startup
     * view exactly: the C++ gsplat-player builds its camera straight from
     * COLMAP's c2w matrix, so matching it means using COLMAP's rotation.
     *
     * Requires a usable images.txt hero pose - and therefore that
     * `cameraSpawn` is NOT set, since that shortcut skips reading the
     * camera file at all. Ignored otherwise.
     *
     * Caveat: the viewer's camera is a look-at camera, so it cannot carry
     * ROLL. A COLMAP camera with roll (v00 in `musical` has 2.75 deg) will
     * match in position, direction and FOV, but sit level where the
     * reference is slightly tilted.
     */
    heroCameraOrientation?: boolean;
    /**
     * Enable the Shift+K renderer cross-check capture for this dataset: step
     * through a spread of its COLMAP cameras, render each at the camera's own
     * exact pose and focal length, and write the frames as
     * `ftgs-supersplat-N.png` into a folder the operator picks once per
     * session (point it at the C++ player's own capture folder and the two
     * sets land side by side).
     *
     * Requires images.txt/cameras.txt, and is a comparison tool rather than a
     * viewer feature - so it is opt-in, and for any dataset that leaves it
     * unset the key listener is never even registered.
     */
    colmapSnapshotKey?: boolean;
    /**
     * Start this dataset paused on its first frame instead of playing
     * immediately, so it opens as a still image and only animates once the
     * viewer presses play. Matches the C++ reference player's own
     * `start_paused` for the same sequence. Off by default; the existing
     * `?ftgsPaused=1` URL override still forces a paused start for any
     * dataset, exactly as before.
     */
    startPaused?: boolean;
    /**
     * Timing of this dataset's data - encoded frame rate (`sourceFps`,
     * default 30) and presentation rate (`presentHz`, default 60, 0 =
     * every tick); see ftgs/playback-timing.ts. Both optional, so existing
     * dataset.json files need no change. Playback speed is NOT configurable
     * here: every dataset starts at 1x.
     */
    playback?: PlaybackTimingConfig;
    /**
     * Hide the top-left build/version tag while this dataset is showing.
     * That tag exists to answer "which build is this browser actually
     * running?" during remote testing (see where it's created in main.ts),
     * which is worth the pixels normally - but a dataset being shown to an
     * audience rather than debugged doesn't want it in frame. Off by
     * default, and re-applied on every dataset switch, so leaving it unset
     * keeps the tag exactly as it has always been.
     */
    hideVersionTag?: boolean;
    /**
     * LEGACY: mp4 filename whose first frame used to serve as the picker
     * thumbnail (via <video preload="metadata">, which cost a multi-hundred-
     * KB Range fetch per dataset just to show one still). Superseded by
     * `thumbnail` (2026-08-28) - kept only as a fallback so an old
     * dataset.json that hasn't been migrated still gets a picker image.
     */
    video?: string;
    /**
     * Low-quality (subsampled) segment tier, produced offline by the
     * sibling ftgs-lq-encoder tool into `uploads/<id>/low/`. Both fields
     * are maintained BY HAND (the encoder deliberately never edits this
     * file): `available` declares the low/ tree exists and gates the
     * viewer's Low option; `defaultTier` optionally pins which tier a
     * fresh visitor starts on (a user's own localStorage choice still
     * wins - see ftgs/quality-tier.ts's resolution order).
     */
    lowQuality?: {
        available?: boolean;
        defaultTier?: 'high' | 'low';
    };
    /**
     * Where the segments come from (P2, docs/p2-browser-bin-decode-design.md):
     *   - absent / 'ply': `segment_NNN.ply` files served by the static
     *     server (the original path)
     *   - 'bin': the folder holds GSC-VSEI `segment_NNN.bin` bitstreams
     *     instead; the viewer decodes them in the browser (ftgs/bin-
     *     prepare.ts) into OPFS, and the service worker serves those
     *     decodes under the very same `segment_NNN.ply` URLs - so
     *     everything downstream of the URL is identical for both sources.
     *     `lowQuality` does not apply to bin datasets.
     */
    source?: 'ply' | 'bin';
    /**
     * Which GSC-VSEI velocity-quantisation arm this dataset was encoded
     * with (`ours_gauss_*` / `ours_lin_*`). Purely descriptive - nothing in
     * the decode path reads it, since the arm is already carried per-video
     * inside the bitstream (SEIGSIQuantizationIdc). It exists so the stats
     * overlay's read-only badge (ui/ftgs-source-badges.ts) can say which
     * arm is on screen. Absent = the badge keeps its Gauss default.
     */
    quantization?: 'lin' | 'gauss';
    /**
     * Rate point this dataset was encoded at - the `N` of the source
     * bitstream's `ours_<arm>_rN` folder. Descriptive only, for the stats
     * overlay's Rate badge. Absent = the badge shows r0.
     */
    rate?: number;
    /**
     * Which rate points exist for this content, e.g. [0, 1, 3, 5]. Absent =
     * the badge falls back to the standard ladder of whichever arm
     * `quantization` names (gauss: 0/1/3/5, lin: 0-5).
     */
    rates?: number[];
    /**
     * Rate-point subfolder this session is playing, e.g. `ours_gauss_r0`.
     * NOT read from dataset.json - main.ts derives it from the stats
     * overlay's Source Type / Velocity Quant / Rate selection and hands a
     * derived config to startFtgsDataset(). Absent = the legacy flat layout
     * (`uploads/<id>/segment_NNN.{ply,bin}`), which every dataset predating
     * the ladder still uses.
     */
    variantDir?: string;
    /**
     * Does this dataset ship the ETRI rate ladder as subfolders
     * (`uploads/<id>/ours_<arm>_r<N>/bitstream.bin` plus that folder's
     * `decoded/<id>_ours_<arm>_r<N>.ply`)? Only then are the stats
     * overlay's Source Type / Velocity Quant / Rate pills live - a dataset
     * without those folders has nothing to switch to, so its pills stay
     * inert rather than 404-ing on a click.
     */
    ladder?: boolean;
    /**
     * Rate-point folder name pattern for a laddered dataset, with `{quant}`
     * and `{rate}` substituted, e.g. `r{rate}` for makeup's
     * `uploads/makeup/r1/bitstream.bin`. Absent = the ETRI package's own
     * `ours_{quant}_r{rate}`.
     */
    variantDirTemplate?: string;
    /**
     * Which Source Type pills this dataset can serve. Absent = both. makeup
     * ships only bitstreams (no decoded/ ply), so it declares ['bin'].
     */
    sources?: ('ply' | 'bin')[];
    /**
     * Which Velocity Quant arms this dataset was encoded with. Absent = both.
     * makeup was encoded with the linear arm only, so it declares ['lin'].
     */
    quantizations?: ('lin' | 'gauss')[];
    /**
     * Container/SEI generation of the bitstream:
     *   - absent / 'legacy': `[u32 size][Annex-B]` x N, one picture per
     *     video, GSI SEI 1.0 (float params), 10-bit - the HM-WASM path
     *   - 'gof': `[u32 numGofs]{[u32 gofIdx][u32 numVideos]{[u32 vidIdx]
     *     [u32 size][Annex-B]}}`, one picture per chunk, new GSI SEI syntax
     *     (log2_quant_denom), 8-bit Main profile (makeup, 2026-09-28).
     *     Its decode path is not built yet - see
     *     docs/p2-browser-bin-decode-design.md.
     */
    binFormat?: 'legacy' | 'gof';
    /**
     * 'gof' only: apply ETRI's reuse_shared_attributes after each chunk's
     * unpack (decode.py --reuse_attributes, framework of 2026-10-07) -
     * gaussians repeated from the previous chunk keep its lossy attributes,
     * removing the chunk-boundary flicker at low rates. makeup-v3 was
     * delivered with it on; the original makeup decodes without it.
     */
    reuseAttributes?: boolean;
};

const DATASETS_INDEX_URL = '/uploads/datasets.json';

/** `uploads/<id>/` base URL for a dataset, e.g. "/uploads/cart" */
const datasetBaseUrl = (id: string): string => `/uploads/${id}`;

const pad3 = (index: number): string => String(index).padStart(3, '0');

/**
 * The rate-point folder name - `ours_<arm>_r<rate>` (the ETRI package's own
 * layout) unless the dataset declares its own `variantDirTemplate`.
 */
const variantDirName = (quant: 'gauss' | 'lin', rate: number, template = 'ours_{quant}_r{rate}'): string => {
    return template.replace('{quant}', quant).replace('{rate}', String(rate));
};

/**
 * The GSC-VSEI bitstream of clip `index` in a `source: "bin"` dataset.
 * Laddered datasets keep one bitstream per rate-point folder
 * (`uploads/<id>/ours_gauss_r0/bitstream.bin`); legacy ones number them
 * flat (`uploads/<id>/segment_NNN.bin`).
 */
const binSegmentUrl = (config: FtgsDatasetConfig, index: number): string => (config.variantDir ?
    `${datasetBaseUrl(config.id)}/${config.variantDir}/bitstream.bin` :
    `${datasetBaseUrl(config.id)}/segment_${pad3(index)}.bin`);

/**
 * Where the playback pipeline fetches clip `index` from.
 *
 * For a laddered PLY variant this is the real file `extract_ply_all.bat`
 * wrote (`<variant>/decoded/<id>_<variant>.ply`). For a laddered BIN
 * variant it is a URL with NO file behind it: bin-prepare.ts decodes the
 * bitstream into OPFS and sw.ts answers this exact URL from there, so the
 * two sources are indistinguishable downstream. The two must NOT share a
 * URL - the service worker checks OPFS before the network, so a PLY variant
 * pointed at the bin URL would be served the decode instead of the file it
 * is there to compare against.
 */
const segmentPlyUrl = (config: FtgsDatasetConfig, index: number, tier: 'high' | 'low' = 'high'): string => {
    if (config.variantDir) {
        return config.source === 'bin' ?
            `${datasetBaseUrl(config.id)}/${config.variantDir}/segment_${pad3(index)}.ply` :
            `${datasetBaseUrl(config.id)}/${config.variantDir}/decoded/${config.id}_${config.variantDir}.ply`;
    }
    return `${datasetBaseUrl(config.id)}${tier === 'low' ? '/low' : ''}/segment_${pad3(index)}.ply`;
};

/**
 * Fetches `uploads/datasets.json` (the list of folder names) then each
 * folder's own `dataset.json` in parallel. A folder listed in the index but
 * missing/malformed its own dataset.json is skipped with a console warning
 * rather than failing the whole picker - one bad dataset shouldn't take the
 * others down.
 */
async function fetchDatasetConfigs(): Promise<FtgsDatasetConfig[]> {
    // cache: 'no-cache' - force a revalidation on EVERY load rather than
    // trusting the browser's heuristic freshness. serve.mjs sends no
    // Cache-Control for .json, so Chrome invents one from Last-Modified
    // (10% of the file's age) and then serves this file from its own cache
    // WITHOUT asking the server at all - confirmed live on 2026-09-16 in
    // server-requests.log: a retired dataset ("musical", deleted from
    // uploads/ and from this index) kept being fetched for hours after the
    // edit, its 404 on the per-dataset dataset.json below emptied the list,
    // and main.ts threw 'listed no usable datasets' -> black screen. A hard
    // reload does NOT fix that: Chrome's cache bypass only covers resources
    // loaded during navigation, and this is an async fetch after load.
    // no-cache still revalidates cheaply (a 304 with an empty body), it
    // just never skips the round trip. Same reasoning as sw.ts's decision
    // to exclude /uploads/ from the service worker entirely: these are
    // hand-edited config files that must reflect on-disk edits immediately.
    const indexResponse = await fetch(DATASETS_INDEX_URL, { cache: 'no-cache' });
    if (!indexResponse.ok) {
        throw new Error(`failed to fetch ${DATASETS_INDEX_URL}: ${indexResponse.status}`);
    }
    const ids: string[] = await indexResponse.json();

    const results = await Promise.all(ids.map(async (id): Promise<FtgsDatasetConfig | null> => {
        try {
            const response = await fetch(`${datasetBaseUrl(id)}/dataset.json`, { cache: 'no-cache' });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const config = await response.json() as FtgsDatasetConfig;
            if (config.id !== id) {
                console.warn(`[ftgs] dataset.json id "${config.id}" doesn't match its folder name "${id}" - using folder name`);
                config.id = id;
            }
            return config;
        } catch (error) {
            console.warn(`[ftgs] skipping dataset "${id}" - failed to load its dataset.json:`, error);
            return null;
        }
    }));

    return results.filter((config): config is FtgsDatasetConfig => config !== null);
}

export { fetchDatasetConfigs, datasetBaseUrl, binSegmentUrl, segmentPlyUrl, variantDirName };
export type { FtgsDatasetConfig };
